// pages/CategoryExplorer.js
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/apiInterceptor';
import { API_URL } from '../constants/data';


import LoadingScreen from '../components/LoadingScreen';
import FilterPanel from '../components/FilterPanel';
import SearchHistory from '../components/SearchHistory';
import TreemapChart from '../components/TreemapChart';
import UserDataTable from '../components/UserDataTable';
import CompanyCards from '../components/CompanyCards';

import { useCategories } from '../hooks/useCategories';
import { useFilters } from '../hooks/useFilters';
import { useApiData } from '../hooks/useApiData';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useDataPointHandler } from '../hooks/useDataPointHandler';
import { useHistoryHandler } from '../hooks/useHistoryHandler';
import { useApplyFilters } from '../hooks/useApplyFilters';


const CategoryExplorer = () => {
    const navigate = useNavigate();

    const { categories, setCategories, totalCounts, setTotalCounts } = useCategories();
    const [complaintNumbers, setComplaintNumbers] = useState([]);
    const [companyData, setCompanyData] = useState({ converged: [], non_converged: [] });
    const [selectedCompany, setSelectedCompany] = useState(null);
    const { filters, handleFilterChange, updateFilters } = useFilters();
    const { searchHistory, saveToHistory, clearHistory, deleteHistoryItem, selectedHistoryIndex, setSelectedHistoryIndex } = useSearchHistory();
    const {
        isLoading,
        setIsLoading,
        fetchCategories: fetchCategoriesApi,
        fetchSemanticRCA,
        fetchSemanticRCA2,
        fetchUserData,
        fetchCategoryWithCount,
    } = useApiData();

    const getAccessToken = () => {
        return localStorage.getItem("access_token") || "";
    };

    const fetchAllCategories = async (currentFilters = filters) => {
        try {
            setIsLoading(true);

            const CATEGORY_COUNT = "categoryDataCache";
            const CACHE_EXPIRY_HOURS = 24;

            const isCacheValid = (cacheTimestamp) => {
                if (!cacheTimestamp) return false;
                const now = Date.now();
                const age = (now - cacheTimestamp) / (1000 * 60 * 60);
                return age < CACHE_EXPIRY_HOURS;
            };

            const cached = JSON.parse(localStorage.getItem(CATEGORY_COUNT) || "{}");

            // Helper function to fetch complaint numbers for top category
            const fetchTopCategoryComplaintNumbers = async (categories, currentFilters) => {
                const sortedCategories = Object.entries(categories)
                    .filter(([key, value]) => key != null && key !== "null" && value.count > 0)
                    .sort((a, b) => (b[1]?.count || 0) - (a[1]?.count || 0));

                if (sortedCategories.length > 0) {
                    const topCategoryName = sortedCategories[0][0];
                    const start_date = currentFilters.startDate ? new Date(currentFilters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
                    const end_date = currentFilters.endDate ? new Date(currentFilters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

                    try {
                        const response = await axios.post(
                            `${API_URL}/get_semantic_rca`,
                            {
                                query: topCategoryName,
                                skip: 0,
                                size: 0,
                                start_date: start_date,
                                end_date: end_date,
                                value: currentFilters.value || 2,
                                CityName: currentFilters.CityName || "All",
                                stateName: currentFilters.stateName || "All",
                                complaintType: currentFilters.complaintType || "All",
                                complaintMode: currentFilters.complaintMode || "All",
                                companyName: currentFilters.companyName || "All",
                                complaintStatus: currentFilters.complaintStatus || "All",
                                threshold: currentFilters.threshold || 1.5,
                                complaint_numbers: ["NA"],
                            },
                            {
                                headers: {
                                    Accept: "application/json",
                                    "Content-Type": "application/json",
                                    'Authorization': `Bearer ${getAccessToken()}`
                                },
                                timeout: 45000,
                            }
                        );

                        const complaintNumbersFromApi = response.data?.complaintNumbers || ["NA"];

                        // Update the top category with complaint numbers
                        const updatedCategories = { ...categories };
                        return { updatedCategories, complaintNumbers: complaintNumbersFromApi };
                    } catch (error) {
                        console.error(`Error fetching complaint numbers for ${topCategoryName}:`, error);
                        return { updatedCategories: categories, complaintNumbers: [] };
                    }
                }
                return { updatedCategories: categories, complaintNumbers: [] };
            };

            if (cached?.timestamp && isCacheValid(cached.timestamp)) {
                console.log("Using cached data", cached);
                setCategories(cached.data.categories || {});
                setTotalCounts(cached.count || cached.data.totalComplaints || 0);

                // Fetch complaint numbers for top category even from cached data
                const { updatedCategories, complaintNumbers: fetchedComplaintNumbers } = await fetchTopCategoryComplaintNumbers(cached.data.categories || {}, currentFilters);

                setCategories(updatedCategories);
                setComplaintNumbers(fetchedComplaintNumbers);

                setIsLoading(false);
                return;
            }

            const start_date = filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
            const end_date = filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];


            const res = await api.post(
                `${API_URL}/complaint_distribution_by_es`,
                {
                    start_date: start_date,
                    end_date: end_date,
                    stateName: "All",
                    sectorName: "All",
                    categoryName: "All",
                    attribute: "sectorName",
                    skip: 0,
                    size: 35,
                }
            );

            const data = res.data || [];
            let totalComplaints = 0;
            const baseCategories = data.reduce((acc, item) => {
                if (item.category != null && item.category !== "null") {
                    acc[item.category] = {
                        prompt: item.categoryPrompt || "",
                        count: item.count,
                        complaintNumbers: ["NA"]
                    }
                }
                totalComplaints += item.count;
                return acc;
            }, {})

            setCategories(baseCategories)
            setTotalCounts(totalComplaints)

            // Fetch complaint numbers for top category
            const { updatedCategories, complaintNumbers: fetchedComplaintNumbers } = await fetchTopCategoryComplaintNumbers(baseCategories, currentFilters);

            setCategories(updatedCategories);
            setComplaintNumbers(fetchedComplaintNumbers);

            // Cache with updated complaint numbers
            localStorage.setItem(
                CATEGORY_COUNT,
                JSON.stringify({
                    timestamp: Date.now(),
                    data: {
                        categories: updatedCategories,
                        totalComplaints,
                    },
                })
            );

            setIsLoading(false);
        } catch (err) {
            console.error("Error fetching all categories:", err);
            setIsLoading(false);
        }
    };

    const fetchCompanyData = async (currentFilters = filters) => {
        try {
            setIsLoading(true);

            const COMPANY_CACHE = "companyDataCache";
            const SUB_COMPANY_CACHE = "subCompanyCache";
            const CACHE_EXPIRY_HOURS = 24;

            const isCacheValid = (cacheTimestamp) => {
                if (!cacheTimestamp) return false;
                const now = Date.now();
                const age = (now - cacheTimestamp) / (1000 * 60 * 60);
                return age < CACHE_EXPIRY_HOURS;
            };

            // Determine the query - check filters and also subcategory cache
            let effectiveQuery = currentFilters.query || '';
            let effectiveComplaintNumbers = complaintNumbers?.length > 0
                ? complaintNumbers
                : (currentFilters.complaintNumbers || currentFilters.complaint_numbers || ['NA']);

            // If no query in filters, check subcategory cache for the prompt
            if (!effectiveQuery) {
                try {
                    const subCatCached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
                    if (subCatCached?.prompt && subCatCached?.timestamp) {
                        const age = (Date.now() - subCatCached.timestamp) / (1000 * 60 * 60);
                        if (age < CACHE_EXPIRY_HOURS) {
                            effectiveQuery = subCatCached.query || subCatCached.prompt;
                            if (subCatCached.complaintNumbers) {
                                effectiveComplaintNumbers = subCatCached.complaintNumbers;
                            }
                        }
                    }
                } catch (e) {
                    console.warn("Failed to check subcategory cache for query:", e);
                }
            }

            const hasQuery = effectiveQuery && effectiveQuery.trim() !== '';

            // Check the appropriate cache
            if (hasQuery) {
                // For queries, check subCompanyCache
                const cached = JSON.parse(localStorage.getItem(SUB_COMPANY_CACHE) || "{}");
                if (cached?.query === effectiveQuery && cached?.timestamp && isCacheValid(cached.timestamp)) {
                    console.log("Using subCompanyCache for query:", effectiveQuery);
                    setCompanyData(cached.data || { converged: [], non_converged: [] });
                    setIsLoading(false);
                    return;
                }
            } else {
                // For no-query (top-level), check companyDataCache
                const cached = JSON.parse(localStorage.getItem(COMPANY_CACHE) || "{}");
                if (cached?.timestamp && isCacheValid(cached.timestamp)) {
                    console.log("Using companyDataCache (no query)");
                    setCompanyData(cached.data || { converged: [], non_converged: [] });
                    setIsLoading(false);
                    return;
                }
            }

            console.log("Fetching company data with query:", effectiveQuery, "complaintNumbers:", effectiveComplaintNumbers);

            const start_date = currentFilters.startDate ? new Date(currentFilters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
            const end_date = currentFilters.endDate ? new Date(currentFilters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

            const response = await api.post(
                `${API_URL}/get_all_company_distribution`,
                {
                    query: effectiveQuery,
                    start_date: start_date,
                    end_date: end_date,
                    purchaseCity: currentFilters.CityName || 'All',
                    complaintMode: currentFilters.complaintMode || 'All',
                    complaintStatus: currentFilters.complaintStatus || 'All',
                    stateName: currentFilters.stateName || 'All',
                    value: currentFilters.value || 1,
                    companyName: currentFilters.companyName || "All",
                    threshold: currentFilters.threshold || 1.5,
                    complainNumbers: effectiveComplaintNumbers,
                    skip: 0,
                    size: 25,
                }
            );

            const apiData = response.data || {};

            const processedData = {
                converged: (apiData.converged || []).slice(0, 25),
                non_converged: (apiData.non_converged || []).slice(0, 25)
            };

            setCompanyData(processedData);

            // Cache with the correct key
            const cacheKey = hasQuery ? SUB_COMPANY_CACHE : COMPANY_CACHE;
            const cacheData = {
                timestamp: Date.now(),
                data: processedData,
            };

            if (hasQuery) {
                cacheData.query = effectiveQuery;
            }

            localStorage.setItem(cacheKey, JSON.stringify(cacheData));

            setIsLoading(false);
        } catch (err) {
            console.error("Error fetching company data:", err);
            setIsLoading(false);
        }
    };

    const checkSubcategoryCache = async () => {
        try {
            setIsLoading(true);

            const cached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
            console.log("cached", cached)
            if (cached?.prompt && cached?.timestamp && cached?.data) {
                const age = (Date.now() - cached.timestamp) / (1000 * 60 * 60);
                if (age < 24) {
                    setCategories(cached.data.categories || {});
                    setTotalCounts(cached.count || 0);
                    // Use cached.query (Short Name) if available
                    updateFilters({ query: cached.query || cached.prompt, complaintNumbers: cached.complaintNumbers });

                    setComplaintNumbers(cached.complaintNumbers);
                    setIsLoading(false);
                    return true;
                }
            }

            setIsLoading(false);
            return false;
        } catch (error) {
            console.warn("Failed to check subcategory cache:", error);
            setIsLoading(false);
            return false;
        }
    };

    const hasInitialized = useRef(false);

    const initializeData = async () => {
        const usedCache = await checkSubcategoryCache();
        if (!usedCache) {
            await fetchAllCategories();
        }

        // After initialization, if viewMode is Company-wise, fetch company data
        // with the correct filters (including query/complaintNumbers from cache)
        if (filters.viewMode === 'Company-wise') {
            // Build the correct filters including subcategory cache data
            let initFilters = { ...filters };
            try {
                const cached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
                if (cached?.prompt && cached?.timestamp) {
                    const age = (Date.now() - cached.timestamp) / (1000 * 60 * 60);
                    if (age < 24) {
                        initFilters.query = cached.query || cached.prompt;
                        initFilters.complaintNumbers = cached.complaintNumbers || ['NA'];
                    }
                }
            } catch (e) { /* ignore */ }
            await fetchCompanyData(initFilters);
        }

        hasInitialized.current = true;
    };

    useEffect(() => {
        initializeData();
        return () => {
            // cleanup if needed
        };
    }, []);

    // Handle view mode changes (skip during initialization — handled above)
    useEffect(() => {
        if (!hasInitialized.current) return; // Skip until initialization is done

        if (filters.viewMode === 'Company-wise') {
            fetchCompanyData(filters);
        } else {
            // Reset company selection when switching to category-wise
            setSelectedCompany(null);
            if (filters.companyName && filters.companyName !== 'All') {
                updateFilters({ companyName: 'All' });
            }
            // Don't clear complaintNumbers - they should remain for category view
        }
    }, [filters.viewMode]);

    const handleCompanyClick = async (company, type) => {
        try {
            setIsLoading(true);

            const companyName = company.companyName || company.name;

            // Set selected company for display (not in filters)
            setSelectedCompany({
                name: companyName,
                type: type,
                count: company.count || 0,
                disposalRate: company.disposalRate || 0
            });

            // Set complaint numbers to trigger UserDataTable refresh
            setComplaintNumbers(filters.complaintNumbers);
            setIsLoading(false);
        } catch (error) {
            console.error('Error handling company click:', error);
            setIsLoading(false);
        }
    };
    const fetchCategories = async (promptOrCategoryData, currentFilters = filters) => {
        try {
            const { updatedData, totalCount } = await fetchCategoryWithCount(
                promptOrCategoryData,
                setCategories,
                currentFilters
            );
            setCategories(updatedData);
            // setTotalCounts(totalCount);
        } catch (error) {
            console.error('fetchCategories failed:', error);
        }
    };

    const UserData = useCallback((ids) => {
        setComplaintNumbers(ids);
        handleFilterChange({ complaintNumbers: ids });
    }, []);

    const { handleDataPointClick } = useDataPointHandler({
        categories,
        filters,
        handleFilterChange,
        fetchSemanticRCA,
        fetchSemanticRCA2,
        UserData,
        fetchCategories,
        saveToHistory,
        setIsLoading,
        setTotalCounts
    });

    const { handleHistoryClick } = useHistoryHandler({
        filters,
        updateFilters,
        fetchSemanticRCA,
        fetchSemanticRCA2,
        UserData,
        fetchCategories,
        setTotalCounts,
        setIsLoading
    });

    const { handleApplyFilters } = useApplyFilters({
        filters,
        updateFilters,
        fetchSemanticRCA,
        fetchSemanticRCA2,
        UserData,
        fetchCategories,
        saveToHistory,
        totalCounts,
        setTotalCounts,
        setIsLoading
    });

    const handleClearHistory = useCallback(() => {
        clearHistory();
        const resetFilters = { ...filters, query: '', complaintNumbers: ["NA"], complaint_numbers: ["NA"], purchaseCity: "All", stateName: "All", complaintMode: "All", companyName: "All", complaintStatus: "All", value: 1, title: "" };
        updateFilters(resetFilters);

        localStorage.removeItem('dashboardFilters');

        // Fetch root data immediately with reset filters
        fetchAllCategories(resetFilters);
    }, [clearHistory, updateFilters, filters]);

    if (!categories) {
        return <LoadingScreen message="Loading categories..." />;
    }

    return (
        <Box sx={{ p: 2 }}>

            <SearchHistory
                searchHistory={searchHistory}
                onHistoryClick={handleHistoryClick}
                onClearHistory={handleClearHistory}
                onDeleteHistoryItem={deleteHistoryItem}
                selectedHistoryIndex={selectedHistoryIndex}
                setSelectedHistoryIndex={setSelectedHistoryIndex}
            />

            <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                isLoading={isLoading}
                searchHistory={searchHistory}
                clearHistory={handleClearHistory}
                showViewMode={true}
            />

            {/* Selected Company Display - Only in Company-wise mode */}
            {filters.viewMode === 'Company-wise' && selectedCompany && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={`Selected: ${selectedCompany.name}`}
                        onDelete={() => {
                            setSelectedCompany(null);
                            setComplaintNumbers([]);
                        }}
                        color={selectedCompany.type === 'convergence' ? 'success' : 'warning'}
                        sx={{
                            fontWeight: 600,
                            '& .MuiChip-label': {
                                fontSize: '13px'
                            }
                        }}
                    />
                    <Chip
                        label={`${selectedCompany.count?.toLocaleString()} complaints`}
                        size="small"
                        variant="outlined"
                    />
                    <Chip
                        label={`${selectedCompany.disposalRate?.toFixed(1)}% disposal rate`}
                        size="small"
                        variant="outlined"
                    />
                </Box>
            )}

            <Box sx={{
                minHeight: { xs: '450px', sm: '450px', md: '450px', lg: '450px', xl: '450px' },
                position: 'relative',
                overflow: 'visible',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                p: filters.viewMode === 'Company-wise' ? 0 : 0
            }}>
                {filters.viewMode === 'Company-wise' ? (
                    <CompanyCards
                        companyData={companyData}
                        onCompanyClick={handleCompanyClick}
                        isLoading={isLoading}
                    />
                ) : (
                    <TreemapChart
                        categories={categories}
                        totalCounts={totalCounts}
                        onDataPointClick={handleDataPointClick}
                        isLoading={isLoading}
                    />
                )}
            </Box>

            <UserDataTable
                complaintNumbers={complaintNumbers}
                viewMode={filters.viewMode || 'Category-wise'}
                filters={filters}
                selectedCompany={selectedCompany}
            />
        </Box>
    );
};

export default CategoryExplorer;